import { useLocation } from "wouter";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Terms() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setLocation("/")}
            className="flex items-center gap-2"
            data-testid="button-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <a href="/" className="font-serif text-xl font-semibold tracking-tight">
            <span className="text-primary">Life</span> Itself Studio
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <h1 className="font-serif text-4xl font-semibold mb-2" data-testid="text-terms-title">
            Terms of Service
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            Last Updated: January 2, 2026
          </p>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-foreground/90 leading-relaxed">
              Welcome to Life Itself Studio! These Terms of Service ("Terms") govern your access to and use of
              the Life Itself Studio website, products, and services (collectively, the "Services") operated by
              Life Itself Studio LLC ("Company," "we," "us," or "our"). By accessing or using our Services, you
              agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
            </p>
          </section>

          {/* 1. Acceptance of Terms */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-acceptance">
              1. Acceptance of Terms
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                By accessing, browsing, or using our website at lifeitself.studio, placing an order, or
                otherwise engaging with our Services, you acknowledge that you have read, understood, and
                agree to be bound by these Terms and our Privacy Policy.
              </p>
              <p>
                You must be at least 18 years of age or the age of majority in your jurisdiction to use our
                Services. By using our Services, you represent and warrant that you meet these requirements.
              </p>
              <p>
                We reserve the right to modify these Terms at any time. We will notify you of any material
                changes by posting the updated Terms on our website with a new "Last Updated" date. Your
                continued use of the Services after such modifications constitutes your acceptance of the
                revised Terms.
              </p>
            </div>
          </section>

          {/* 2. User Account Responsibilities */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-accounts">
              2. User Account Responsibilities
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                When you place an order or create an account with us, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, current, and complete information during registration and ordering</li>
                <li>Maintain and promptly update your information to keep it accurate and complete</li>
                <li>Maintain the security and confidentiality of any account credentials or order access links</li>
                <li>Notify us immediately of any unauthorized use of your account or any security breach</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate your account if any information provided is
                inaccurate, incomplete, or otherwise violates these Terms.
              </p>
            </div>
          </section>

          {/* 3. Prohibited Activities */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-prohibited">
              3. Prohibited Activities
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                When using our Services, you agree NOT to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Services for any unlawful purpose or in violation of any applicable laws</li>
                <li>Impersonate any person or entity, or falsely state or misrepresent your affiliation</li>
                <li>Interfere with or disrupt the Services or servers connected to the Services</li>
                <li>Attempt to gain unauthorized access to any portion of the Services or related systems</li>
                <li>Use automated systems, bots, or scripts to access the Services without our permission</li>
                <li>Collect or harvest personal information of other users</li>
                <li>Submit false or fraudulent orders or payment information</li>
                <li>Resell or commercially exploit the Services without our express written consent</li>
                <li>Use the Services to transmit spam, chain letters, or unsolicited communications</li>
                <li>Engage in any activity that could damage, disable, or impair the Services</li>
                <li>Reverse engineer, decompile, or disassemble any portion of the Services</li>
              </ul>
              <p>
                Violation of these prohibitions may result in immediate termination of your access to the
                Services and may subject you to legal liability.
              </p>
            </div>
          </section>

          {/* 4. Intellectual Property Rights */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-ip">
              4. Intellectual Property Rights
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                All content, features, and functionality of the Services, including but not limited to text,
                graphics, logos, images, photographs, designs, product descriptions, software, and the
                overall look and feel of the website (collectively, "Content"), are owned by Life Itself Studio,
                our licensors, or other content providers and are protected by United States and international
                copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p>
                The Life Itself Studio name, logo, and all related names, logos, product and service names,
                designs, and slogans are trademarks of Life Itself Studio or its affiliates. You may not use
                such marks without our prior written permission.
              </p>
              <p>
                You are granted a limited, non-exclusive, non-transferable, revocable license to access
                and use the Services for personal, non-commercial purposes only. This license does not
                include the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Modify, copy, or create derivative works based on the Services or Content</li>
                <li>Use any data mining, robots, or similar data gathering methods</li>
                <li>Download any Content except as expressly authorized</li>
                <li>Use the Content for any commercial purpose without written consent</li>
              </ul>
            </div>
          </section>

          {/* 5. User-Generated Content */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-ugc">
              5. User-Generated Content
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                Our Services may allow you to submit, post, or share content such as reviews, comments,
                photos, or other materials ("User Content"). By submitting User Content, you:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Grant Life Itself Studio a non-exclusive, worldwide, royalty-free, perpetual, irrevocable
                  license to use, reproduce, modify, adapt, publish, translate, distribute, and display
                  such User Content in any media
                </li>
                <li>
                  Represent and warrant that you own or have the necessary rights to submit the User
                  Content and to grant the above license
                </li>
                <li>
                  Represent that the User Content does not violate any third party's rights, including
                  intellectual property, privacy, or publicity rights
                </li>
                <li>
                  Acknowledge that User Content may be publicly viewable by other users
                </li>
              </ul>
              <p>
                We reserve the right, but not the obligation, to monitor, edit, or remove any User Content
                that we determine, in our sole discretion, violates these Terms or is otherwise objectionable.
              </p>
              <p>
                You are solely responsible for your User Content and the consequences of posting it. We
                do not endorse any User Content or any opinion, recommendation, or advice expressed therein.
              </p>
            </div>
          </section>

          {/* 6. Products and Orders */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-products">
              6. Products and Orders
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Product Descriptions:</strong> We strive to display our terrariums and products as
                accurately as possible. However, as each terrarium is handcrafted and contains living
                plants, actual products may vary slightly from images shown. Colors, sizes, and plant
                varieties may differ based on availability and natural variations.
              </p>
              <p>
                <strong>Availability:</strong> All products are subject to availability. We reserve the
                right to limit quantities, discontinue products, or modify offerings without prior notice.
              </p>
              <p>
                <strong>Order Acceptance:</strong> Your order constitutes an offer to purchase. We reserve
                the right to accept or decline your order for any reason. Order confirmation does not
                constitute acceptance. An order is accepted only when the product is shipped or, for
                pre-orders, when we begin crafting your terrarium.
              </p>
              <p>
                <strong>Living Products:</strong> Our terrariums contain living plants. By ordering, you
                acknowledge that plants are living organisms subject to natural processes and that minor
                variations in appearance are normal and expected.
              </p>
            </div>
          </section>

          {/* 7. Payment Terms */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-payment">
              7. Payment Terms
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Pricing:</strong> All prices are displayed in U.S. dollars and are subject to
                change without notice. Prices do not include applicable taxes or shipping charges, which
                will be added at checkout.
              </p>
              <p>
                <strong>Payment Methods:</strong> We accept major credit cards, debit cards, and other
                payment methods as displayed at checkout. All payments are processed securely through
                our third-party payment processor.
              </p>
              <p>
                <strong>Payment Processing:</strong> For standard orders, payment is processed at the
                time of order. For pre-orders and custom orders, payment may be processed at the time
                of order or at a specified point before fulfillment, as indicated during checkout.
              </p>
              <p>
                <strong>Failed Payments:</strong> If a payment fails or is declined, we may attempt to
                process it again or contact you for alternative payment. Orders with failed payments
                may be cancelled.
              </p>
              <p>
                <strong>Refunds:</strong> Refund policies vary by product type and are detailed in our
                separate Refund Policy. Generally, due to the living nature of our products, we offer
                limited refunds for issues reported within 48 hours of delivery. Custom orders and
                experience bookings may have different refund terms.
              </p>
            </div>
          </section>

          {/* 8. Shipping and Delivery */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-shipping">
              8. Shipping and Delivery
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Delivery Area:</strong> We currently offer delivery within the Denver, Colorado
                metropolitan area. Delivery availability may expand over time.
              </p>
              <p>
                <strong>Delivery Timing:</strong> Delivery dates are estimates and are not guaranteed.
                We will make reasonable efforts to deliver within the specified timeframe but are not
                liable for delays caused by circumstances beyond our control.
              </p>
              <p>
                <strong>Risk of Loss:</strong> Risk of loss and title for products pass to you upon
                delivery to the specified address.
              </p>
              <p>
                <strong>Delivery Issues:</strong> Please inspect your terrarium upon delivery. Any damage
                or issues must be reported within 48 hours of delivery to be eligible for replacement
                or refund consideration.
              </p>
            </div>
          </section>

          {/* 9. Dispute Resolution */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-disputes">
              9. Dispute Resolution
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Informal Resolution:</strong> Before initiating any formal dispute proceedings,
                you agree to first contact us at hello@lifeitself.studio to attempt to resolve any
                dispute informally. We will make good faith efforts to resolve any issue within 30 days.
              </p>
              <p>
                <strong>Binding Arbitration:</strong> If we cannot resolve a dispute informally, you
                agree that any dispute, claim, or controversy arising out of or relating to these Terms
                or the Services shall be resolved through binding arbitration administered by the
                American Arbitration Association ("AAA") in accordance with its Commercial Arbitration Rules.
              </p>
              <p>
                <strong>Arbitration Location:</strong> Arbitration shall take place in Denver, Colorado,
                or may be conducted remotely as determined by the arbitrator.
              </p>
              <p>
                <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings
                will be conducted only on an individual basis and not in a class, consolidated, or
                representative action. You waive any right to participate in class actions.
              </p>
              <p>
                <strong>Small Claims Court:</strong> Notwithstanding the above, either party may bring
                an individual action in small claims court for disputes within that court's jurisdiction.
              </p>
              <p>
                <strong>Exceptions:</strong> Nothing in this section prevents either party from seeking
                injunctive or other equitable relief from a court of competent jurisdiction for matters
                relating to intellectual property or unauthorized access to the Services.
              </p>
            </div>
          </section>

          {/* 10. Limitation of Liability */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-liability">
              10. Limitation of Liability
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p className="uppercase font-medium">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <p>
                <strong>Disclaimer of Warranties:</strong> THE SERVICES AND ALL PRODUCTS ARE PROVIDED
                "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES
                WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
              </p>
              <p>
                <strong>Limitation of Damages:</strong> IN NO EVENT SHALL LIFE ITSELF STUDIO, ITS OFFICERS,
                DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO
                DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING
                OUT OF OR RELATING TO YOUR USE OF OR INABILITY TO USE THE SERVICES.
              </p>
              <p>
                <strong>Maximum Liability:</strong> OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING
                FROM OR RELATING TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF (A)
                THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE
                HUNDRED DOLLARS ($100).
              </p>
              <p>
                <strong>Living Products:</strong> Due to the living nature of our products, we cannot
                guarantee the long-term survival of plants. Proper care instructions are provided with
                each terrarium, and plant health depends on adherence to these guidelines.
              </p>
              <p>
                Some jurisdictions do not allow the exclusion of certain warranties or limitations of
                liability, so some of the above limitations may not apply to you.
              </p>
            </div>
          </section>

          {/* 11. Indemnification */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-indemnification">
              11. Indemnification
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                You agree to indemnify, defend, and hold harmless Life Itself Studio, its officers, directors,
                employees, agents, licensors, and affiliates from and against any and all claims,
                liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees)
                arising out of or relating to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of or access to the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Any User Content you submit, post, or share through the Services</li>
                <li>Your negligent or wrongful conduct</li>
              </ul>
              <p>
                We reserve the right to assume exclusive defense and control of any matter subject to
                indemnification by you, in which case you agree to cooperate with our defense of such claim.
              </p>
            </div>
          </section>

          {/* 12. Termination */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-termination">
              12. Termination
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Termination by Us:</strong> We may suspend or terminate your access to the Services
                at any time, with or without cause, with or without notice, effective immediately. Reasons
                for termination may include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violation of these Terms</li>
                <li>Fraudulent, abusive, or illegal activity</li>
                <li>Failure to pay for orders</li>
                <li>Conduct harmful to other users or our business interests</li>
                <li>Extended inactivity</li>
              </ul>
              <p>
                <strong>Termination by You:</strong> You may stop using the Services at any time. If you
                have an account, you may request account deletion by contacting us.
              </p>
              <p>
                <strong>Effect of Termination:</strong> Upon termination, your right to use the Services
                ceases immediately. Provisions of these Terms that by their nature should survive
                termination shall survive, including but not limited to intellectual property provisions,
                warranty disclaimers, limitation of liability, indemnification, and dispute resolution.
              </p>
              <p>
                <strong>Outstanding Orders:</strong> Termination does not affect obligations for orders
                already placed and paid for, though we reserve the right to cancel and refund orders in
                cases of serious Terms violations.
              </p>
            </div>
          </section>

          {/* 13. Governing Law */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-governing-law">
              13. Governing Law
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                These Terms and any dispute arising out of or related to these Terms or the Services
                shall be governed by and construed in accordance with the laws of the State of Colorado,
                United States, without regard to its conflict of law provisions.
              </p>
              <p>
                Subject to the arbitration provisions above, you agree that any legal action or proceeding
                arising out of or relating to these Terms shall be brought exclusively in the state or
                federal courts located in Denver County, Colorado, and you consent to the personal
                jurisdiction of such courts.
              </p>
            </div>
          </section>

          {/* 14. General Provisions */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-general">
              14. General Provisions
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and any
                other legal notices published on the Services, constitute the entire agreement between
                you and Life Itself Studio regarding the Services.
              </p>
              <p>
                <strong>Severability:</strong> If any provision of these Terms is held to be invalid or
                unenforceable, such provision shall be struck and the remaining provisions shall remain
                in full force and effect.
              </p>
              <p>
                <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms
                shall not constitute a waiver of such right or provision.
              </p>
              <p>
                <strong>Assignment:</strong> You may not assign or transfer these Terms or your rights
                hereunder without our prior written consent. We may assign our rights and obligations
                without restriction.
              </p>
              <p>
                <strong>Force Majeure:</strong> We shall not be liable for any failure or delay in
                performance due to circumstances beyond our reasonable control, including natural
                disasters, acts of government, labor disputes, or supply chain disruptions.
              </p>
              <p>
                <strong>Headings:</strong> Section headings are for convenience only and do not affect
                the interpretation of these Terms.
              </p>
            </div>
          </section>

          {/* 15. Contact Information */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-medium mb-4" data-testid="text-section-contact">
              15. Contact Information
            </h2>
            <div className="space-y-4 text-foreground/90">
              <p>
                If you have any questions, concerns, or feedback regarding these Terms of Service,
                please contact us:
              </p>
              <div className="bg-muted/50 rounded-lg p-6 space-y-3">
                <p className="font-medium">Life Itself Studio LLC</p>
                <p>Denver, Colorado</p>
                <a
                  href="mailto:hello@lifeitself.studio"
                  className="flex items-center gap-2 text-primary hover:underline"
                  data-testid="link-contact-email"
                >
                  <Mail className="w-4 h-4" />
                  hello@lifeitself.studio
                </a>
              </div>
              <p className="text-sm text-muted-foreground">
                We aim to respond to all inquiries within 2-3 business days.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="border-t border-border pt-8">
            <p className="text-foreground/90 italic">
              By using Life Itself Studio Services, you acknowledge that you have read these Terms of Service,
              understood them, and agree to be bound by them. If you do not agree to these Terms, you
              must not use our Services.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="/" className="hover:text-foreground transition-colors">Home</a>
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-foreground">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
